import { useState, useEffect, useCallback, Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Quote, Play, Plus, Trash2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import * as api from "../lib/api";
import CitationDiagnosis, { PageLink, PriorityBadge } from "../components/ai-citations/CitationDiagnosis";
import { useOrgId } from "../hooks/useOrgId";
import { useSite } from "../hooks/useSite";

// `https://www.Example.com/x` -> `example.com`, matching the backend's normalize_domain.
function hostOf(url) {
  if (!url) return "";
  try {
    const host = new URL(url.includes("://") ? url : `https://${url}`).hostname.toLowerCase();
    return host.startsWith("www.") ? host.slice(4) : host;
  } catch {
    return "";
  }
}

function formatDate(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function errorText(err, fallback) {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) {
    return detail.next_due_at ? `${detail.message} Next check due ${formatDate(detail.next_due_at)}.` : detail.message;
  }
  return `${fallback}${err.response?.status ? ` (HTTP ${err.response.status})` : ""}.`;
}

function Stat({ label, value, hint }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-2xl font-bold text-zinc-900 mt-1 tabular-nums">{value}</p>
      {hint && <p className="text-[10px] text-zinc-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export default function AiCitationsPage() {
  const orgId = useOrgId();
  const { site } = useSite();
  const siteDomain = hostOf(site?.base_url);

  const [tracked, setTracked] = useState(null); // { queries, quota }
  const [domain, setDomain] = useState("");
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [running, setRunning] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [newQueries, setNewQueries] = useState("");
  const [adding, setAdding] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [detected, setDetected] = useState(null); // Search Console's top country, when none is set
  const [picked, setPicked] = useState(() => new Set());
  const [openId, setOpenId] = useState(null); // query whose "why / what to do" panel is open
  // ?topic=<id> from Search Performance → Topic Clusters narrows the suggestions to that topic.
  const [searchParams] = useSearchParams();
  const topicId = searchParams.get("topic");

  const load = useCallback(async () => {
    setError(null);
    try {
      const q = await api.getAiCitationQueries();
      setTracked(q);
      const domains = [...new Set(q.queries.map((r) => r.domain))].sort();
      // Report on the active site when it is tracked, so the site switcher drives this page.
      const own = q.default_domain || siteDomain;
      const pick = domains.includes(own) ? own : domains[0] || "";
      setDomain(pick);
      setReport(pick ? await api.getAiCitationReport({ domain: pick }) : null);
    } catch (err) {
      console.error("[AiCitations] load failed:", { status: err.response?.status, message: err.message });
      setError(errorText(err, "Couldn't load AI citations"));
    }
  }, [siteDomain]);

  useEffect(() => {
    load();
  }, [orgId, load]);

  // The Search Console property's domain: the site the suggestions come from.
  const ownDomain = tracked?.default_domain || siteDomain;
  useEffect(() => {
    if (!newDomain) setNewDomain(ownDomain || domain);
  }, [ownDomain, domain, newDomain]);

  const loadSuggestions = useCallback(async () => {
    try {
      const params = { limit: 20, ...(newDomain ? { domain: newDomain } : {}), ...(topicId ? { topic_id: topicId } : {}) };
      const res = await api.getAiCitationSuggestions(params);
      setSuggestions(res.suggestions);
      // Coming from a topic: pre-tick its queries so one click tracks them.
      setPicked(new Set(topicId ? res.suggestions.map((x) => x.query) : []));
    } catch (err) {
      console.error("[AiCitations] suggestions failed:", { status: err.response?.status, message: err.message });
      setSuggestions([]);
    }
  }, [newDomain, topicId]);

  useEffect(() => {
    if (tracked) loadSuggestions();
  }, [tracked, loadSuggestions]);

  // No target country saved: ask Search Console where impressions come from and offer it.
  const countrySource = tracked?.country?.source;
  useEffect(() => {
    if (countrySource !== "default") return;
    api.getTargetCountry(true).then((d) => setDetected(d.detected)).catch(() => setDetected(null));
  }, [countrySource]);

  const applyCountry = async (code) => {
    try {
      const res = await api.setTargetCountry(code);
      setNotice(`Checks now run in Google ${res.country.name}` +
        (res.citation_queries_moved ? ` · ${res.citation_queries_moved} tracked queries moved` : "") + ".");
      setDetected(null);
      await load();
    } catch (err) {
      setNotice(errorText(err, "Couldn't save the country"));
    }
  };

  const togglePick = (query) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(query) ? next.delete(query) : next.add(query);
      return next;
    });

  const switchDomain = async (d) => {
    setDomain(d);
    try {
      setReport(await api.getAiCitationReport({ domain: d }));
    } catch (err) {
      setError(errorText(err, "Couldn't load the report"));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const typed = newQueries.split("\n").map((s) => s.trim()).filter(Boolean);
    const queries = [...new Set([...picked, ...typed])];
    if (!queries.length) return;
    setAdding(true);
    setNotice(null);
    try {
      const res = await api.addAiCitationQueries({ domain: newDomain, queries });
      const parts = [`Added ${res.added.length}`];
      if (res.already_tracked?.length) parts.push(`${res.already_tracked.length} already tracked`);
      if (res.refused) parts.push(res.reason || "some refused: query limit reached");
      setNotice(parts.join(" · "));
      setNewQueries("");
      setPicked(new Set());
      await load();
    } catch (err) {
      setNotice(errorText(err, "Couldn't add queries"));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Stop tracking this query? Its snapshot history is deleted too.")) return;
    try {
      await api.deleteAiCitationQuery(id);
      await load();
    } catch (err) {
      setNotice(errorText(err, "Couldn't delete the query"));
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setNotice(null);
    try {
      const res = await api.runAiCitations();
      const parts = [`Checked ${res.snapshots} new or due ${res.snapshots === 1 ? "query" : "queries"}`];
      if (res.not_due) parts.push(`${res.not_due} checked this week, next due ${formatDate(res.next_due_at)}`);
      if (res.errors?.length) parts.push(`${res.errors.length} failed (will retry)`);
      setNotice(parts.join(" · "));
      await load();
    } catch (err) {
      setNotice(errorText(err, "Check failed"));
    } finally {
      setRunning(false);
    }
  };

  const quota = tracked?.quota;
  // Examples from the site's own Search Console queries, never a hard-coded niche.
  const placeholder = suggestions?.length
    ? suggestions.slice(0, 2).map((x) => x.query).join("\n")
    : "One question per line, the way your buyers search";
  const domains = [...new Set((tracked?.queries || []).map((r) => r.domain))].sort();
  const stats = Object.fromEntries((report?.queries || []).map((q) => [q.query_id, q]));
  const rows = (tracked?.queries || []).filter((q) => q.domain === domain);
  const idByQuery = Object.fromEntries(rows.map((q) => [q.query, q.id]));
  const openDiagnosis = (id) => {
    setOpenId(id);
    requestAnimationFrame(() => document.getElementById(`diagnosis-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Quote size={20} strokeWidth={2} className="text-zinc-900" />
          <div>
            <h1 className="text-xl font-bold text-zinc-900">AI Citations</h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              Is your site cited in Google's AI Overviews for the questions you track?
            </p>
          </div>
        </div>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={handleRun}
            disabled={running}
            title="Checks new queries and any not checked in the last 7 days"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 rounded-lg"
          >
            <Play size={14} strokeWidth={2} />
            {running ? "Checking…" : "Check now"}
          </button>
        )}
      </div>

      {quota && (
        <p className="text-xs text-zinc-500 mb-4">
          <span className="capitalize font-bold text-zinc-900">{quota.plan}</span> plan · {quota.queries_used} of{" "}
          {quota.query_limit} queries · each checked automatically once a week
        </p>
      )}

      {tracked?.country && (
        <p className="text-xs text-zinc-500 mb-4 -mt-2">
          Checked in Google <span className="font-bold text-zinc-900">{tracked.country.name}</span>
          {tracked.country.source === "settings" ? " · your target country" : " · default, no target country set"}
          {" · "}
          <Link to="/dashboard/settings/business" className="underline underline-offset-4 hover:text-zinc-900">change</Link>
        </p>
      )}
      {detected?.code && detected.code !== tracked?.country?.code && (
        <div className="flex flex-wrap items-center gap-3 text-xs bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
          <span className="flex-1 min-w-0 text-amber-900">
            {detected.breakdown[0].share_pct}% of your Search Console impressions come from{" "}
            <strong>{detected.breakdown[0].name}</strong>, but checks run in Google {tracked.country.name}.
          </span>
          <button type="button" onClick={() => applyCountry(detected.code)}
            className="px-3 py-1.5 font-bold bg-zinc-900 text-white rounded-lg hover:bg-zinc-800">
            Use {detected.breakdown[0].name}
          </button>
        </div>
      )}

      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
      {notice && <p className="text-xs text-zinc-700 bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2 mb-4">{notice}</p>}
      {!tracked && !error && <p className="text-sm text-zinc-400">Loading…</p>}

      {domains.length > 1 && (
        <div className="flex gap-1 mb-4">
          {domains.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => switchDomain(d)}
              className={`px-3 py-1.5 text-xs rounded-md border ${
                domain === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-100 hover:bg-zinc-50"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      )}

      {report && report.snapshots > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat label="Citation rate" value={`${report.citation_rate}%`} hint={`${report.cited_snapshots} of ${report.snapshots} checks`} />
            <Stat label="Cited first" value={`${report.first_citation_rate}%`} hint="of checks with an AI Overview" />
            <Stat label="AI Overview shown" value={`${report.overview_rate}%`} hint="of checks" />
            <Stat label="Queries" value={report.tracked_queries} hint={`last ${report.window_days} days`} />
          </div>
          <p className="text-[10px] text-zinc-400 mb-6">{report.note}</p>

          {report.fix_first?.length > 0 && (
            <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-5 mb-4">
              <p className="text-sm font-bold text-zinc-900">Fix first</p>
              <p className="text-xs text-zinc-500 mt-0.5 mb-3">
                Lost citations, then the gaps people search most. Open one to see what to change.
              </p>
              <ul className="divide-y divide-zinc-100">
                {report.fix_first.map((f) => (
                  <li key={f.query} className="flex items-center gap-3 py-2 text-xs">
                    <PriorityBadge priority={f.priority} />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-bold text-zinc-900">{f.query}</span>
                      <span className="block text-[10px] text-zinc-500">
                        {f.kind === "lost" ? "Cited on the previous check, not on the latest" : "AI Overview shows, you're not in it"}
                        {f.impressions ? ` · ${f.impressions.toLocaleString()} impressions in Search Console` : ""}
                      </span>
                    </span>
                    {f.query_id && (
                      <button type="button" onClick={() => openDiagnosis(f.query_id)}
                        className="shrink-0 px-2.5 py-1 text-[10px] font-bold border border-zinc-200 rounded-md text-zinc-900 hover:bg-zinc-50">
                        What to do
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(report.gained.length > 0 || report.lost.length > 0) && (
            <div className="flex flex-wrap gap-2 mb-6">
              {report.gained.map((q) => (
                <span key={`g-${q}`} className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-800">
                  <ArrowUpRight size={12} strokeWidth={2} /> {q}
                </span>
              ))}
              {report.lost.map((q) => (
                <span key={`l-${q}`} title="Cited on the previous check, not on the latest"
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-red-50 text-red-800 font-bold">
                  <ArrowDownRight size={12} strokeWidth={2} /> {q}
                </span>
              ))}
            </div>
          )}

          {report.our_pages?.length > 0 && (
            <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-5 mb-4">
              <p className="text-sm font-bold text-zinc-900">Your pages that get cited</p>
              <p className="text-xs text-zinc-500 mt-0.5 mb-3">Protect these: they are what AI Overviews already quote from you.</p>
              <ul className="space-y-2">
                {report.our_pages.map((pg) => (
                  <li key={pg.url} className="text-xs">
                    <div className="flex gap-2">
                      <PageLink url={pg.url} className="flex-1 min-w-0 !text-zinc-900" />
                      <span className="text-zinc-500 tabular-nums">
                        {pg.appearances}× · best #{pg.best_position}
                      </span>
                    </div>
                    <p className="text-[10px] text-zinc-400 truncate">{pg.queries.join(" · ")}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-5">
              <p className="text-sm font-bold text-zinc-900">Gaps</p>
              <p className="text-xs text-zinc-500 mt-0.5 mb-3">
                An AI Overview showed but didn't cite you on the latest check. The pages it cited instead:
              </p>
              {report.gap_details.length === 0 ? (
                <p className="text-xs text-zinc-400">No gaps on the latest checks.</p>
              ) : (
                <ul className="space-y-3">
                  {report.gap_details.map((g) => (
                    <li key={g.query} className="text-xs">
                      <div className="flex items-baseline gap-2">
                        <p className="flex-1 min-w-0 text-zinc-900 font-bold">
                          {g.query}
                          {g.impressions > 0 && <span className="ml-1.5 font-normal text-[10px] text-zinc-400">{g.impressions.toLocaleString()} impr</span>}
                        </p>
                        {idByQuery[g.query] && (
                          <button type="button" onClick={() => openDiagnosis(idByQuery[g.query])}
                            className="shrink-0 text-[10px] text-zinc-500 hover:text-zinc-900 underline underline-offset-4">
                            What to do
                          </button>
                        )}
                      </div>
                      {g.cited.slice(0, 3).map((c) => (
                        <div key={c.url} className="flex gap-1.5 mt-0.5">
                          <span className="w-5 shrink-0 text-right tabular-nums text-zinc-400">#{c.position}</span>
                          <PageLink url={c.url} className="flex-1 min-w-0" />
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-5">
              <p className="text-sm font-bold text-zinc-900">Cited instead of you</p>
              <p className="text-xs text-zinc-500 mt-0.5 mb-3">Sites in the same AI Overviews, by share of checks.</p>
              {report.competitors.length === 0 ? (
                <p className="text-xs text-zinc-400">No other sites cited yet.</p>
              ) : (
                <ul className="space-y-2.5">
                  {report.competitors.map((c) => (
                    <li key={c.source} className="text-xs">
                      <div className="flex items-center gap-2">
                        <span className="flex-1 truncate text-zinc-900 font-bold">{c.source}</span>
                        <span className="w-20 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <span className="block h-full bg-zinc-900" style={{ width: `${Math.min(100, c.share_pct)}%` }} />
                        </span>
                        <span className="w-12 text-right tabular-nums text-zinc-500">{c.share_pct}%</span>
                      </div>
                      {(c.pages || []).map((pg) => (
                        <div key={pg.url} className="flex gap-1.5 mt-0.5">
                          <PageLink url={pg.url} className="flex-1 min-w-0" />
                          {pg.appearances > 1 && <span className="text-[10px] text-zinc-400 tabular-nums">×{pg.appearances}</span>}
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      {rows.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-x-auto mb-6">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-2.5 font-bold">Query</th>
                <th className="px-3 py-2.5 font-bold text-right">Checks</th>
                <th className="px-3 py-2.5 font-bold text-right">Cited</th>
                <th className="px-3 py-2.5 font-bold text-right">Best position</th>
                <th className="px-3 py-2.5 font-bold text-right">Last cited</th>
                <th className="px-3 py-2.5" />
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((q) => {
                const s = stats[q.id];
                const open = openId === q.id;
                return (
                  <Fragment key={q.id}>
                  <tr className={open ? "bg-zinc-50" : ""}>
                    <td className="px-5 py-2.5 text-zinc-900">{q.query}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-zinc-500">{s?.runs ?? 0}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-zinc-900">{s ? `${s.citation_rate}%` : "–"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-zinc-500">{s?.best_position ? `#${s.best_position}` : "–"}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-500">{formatDate(s?.last_cited_at)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => (open ? setOpenId(null) : openDiagnosis(q.id))}
                        title="Why pages get cited here, and what to change on yours"
                        className="text-[10px] font-bold text-zinc-500 hover:text-zinc-900 whitespace-nowrap"
                      >
                        {open ? "Close" : s?.cited_runs ? "Why cited?" : "Why not?"}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(q.id)}
                        className="text-zinc-300 hover:text-red-500 p-1"
                        title="Stop tracking"
                      >
                        <Trash2 size={14} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr id={`diagnosis-${q.id}`}>
                      <td colSpan={7} className="px-5 py-4 bg-zinc-50/60">
                        <CitationDiagnosis queryId={q.id} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tracked && (
        <form onSubmit={handleAdd} className="bg-white rounded-xl border border-zinc-100 shadow-sm p-5">
          <p className="text-sm font-bold text-zinc-900">{rows.length ? "Track more questions" : "Start tracking"}</p>
          <p className="text-xs text-zinc-500 mt-0.5 mb-3">
            Pick from your Search Console or write your own, the way your buyers search. Each is checked in
            Google {tracked?.country?.name || "Indonesia"} once a week; press Check now to run new ones straight away.
          </p>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Your domain</label>
          <input
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="example.com"
            className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900"
          />
          <p className="text-[10px] text-zinc-400 mt-1 mb-3">
            {tracked?.default_domain && newDomain === tracked.default_domain
              ? "From your Search Console property."
              : tracked?.default_domain
                ? <>Your Search Console property is <button type="button" onClick={() => setNewDomain(tracked.default_domain)} className="underline underline-offset-4 hover:text-zinc-900">{tracked.default_domain}</button>.</>
                : "Connect Search Console in Settings to fill this in and get suggestions."}
          </p>
          {suggestions?.length > 0 && (
            <div className="mb-3">
              <div className="flex items-baseline justify-between mb-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  Suggested from your Search Console
                </label>
                <button
                  type="button"
                  onClick={() => setPicked(picked.size === suggestions.length ? new Set() : new Set(suggestions.map((x) => x.query)))}
                  className="text-[10px] text-zinc-500 hover:text-zinc-900 underline underline-offset-4"
                >
                  {picked.size === suggestions.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <p className="text-[10px] text-zinc-400 mb-2">
                Real searches from your top topics, questions first: AI Overviews mostly answer questions.
              </p>
              <div className="border border-zinc-100 rounded-lg divide-y divide-zinc-100 max-h-72 overflow-y-auto">
                {suggestions.map((x) => (
                  <label key={x.query} className="flex items-start gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-zinc-50">
                    <input
                      type="checkbox"
                      checked={picked.has(x.query)}
                      onChange={() => togglePick(x.query)}
                      className="mt-0.5"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-zinc-900">{x.query}</span>
                      <span className="block text-[10px] text-zinc-400">
                        {x.topic_label || "No topic"} · {x.impressions.toLocaleString()} impr
                        {x.position ? ` · pos ${Math.round(x.position)}` : ""}
                        {x.intent ? ` · ${x.intent.replace("_", " ")}` : ""}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
            {suggestions?.length ? "Or type your own" : "Questions"}
          </label>
          <textarea
            value={newQueries}
            onChange={(e) => setNewQueries(e.target.value)}
            rows={5}
            placeholder={placeholder}
            className="w-full mb-3 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900"
          />
          <button
            type="submit"
            disabled={adding || !newDomain || (!newQueries.trim() && picked.size === 0)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-zinc-100 bg-white text-zinc-900 hover:bg-zinc-50 disabled:opacity-50 rounded-lg"
          >
            <Plus size={14} strokeWidth={2} />
            {adding ? "Adding…" : picked.size ? `Add ${picked.size + newQueries.split("\n").filter((l) => l.trim()).length} questions` : "Add questions"}
          </button>
          {quota && <p className="text-[10px] text-zinc-400 mt-2">{quota.queries_remaining} of {quota.query_limit} left on your plan.</p>}
        </form>
      )}
    </div>
  );
}
